import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

export type Advisor = {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  company: string;
  email: string | null;
  phone: string | null;
  photo_url: string | null;
  photo_position: string | null;
  display_order: number;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
};

// Public-facing focal point for the advisor photo. The card images use
// object-fit: cover; without object-position the browser centres (50%) and
// crops the top of faces. We bias to "center 22%" and let an admin override it
// per advisor via `photo_position`, guarding a malformed value so it falls back
// to the default instead of silently breaking the crop.
const SAFE_OBJECT_POSITION =
  /^(left|center|right|top|bottom|\d{1,3}%)(\s+(left|center|right|top|bottom|\d{1,3}%))?$/i;

export function resolvePhotoPosition(value?: string | null): string {
  const v = value?.trim();
  return v && SAFE_OBJECT_POSITION.test(v) ? v : 'center 22%';
}

const COLUMNS =
  'id, first_name, last_name, title, company, email, phone, photo_url, photo_position, display_order, is_active, is_default, created_at';

export async function listAdvisors(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<Advisor[]> {
  const { data, error } = await client
    .from('advisors')
    .select(COLUMNS)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return (data ?? []) as Advisor[];
}

export type AdvisorInput = {
  first_name: string;
  last_name: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  photo_url?: string;
  photo_position?: string;
};

export async function createAdvisor(
  input: AdvisorInput,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<Advisor> {
  const { data, error } = await client.from('advisors').insert(input).select(COLUMNS).single();

  if (error) throw error;
  return data as Advisor;
}

export async function updateAdvisor(
  id: string,
  patch: Partial<AdvisorInput> & { is_active?: boolean; display_order?: number },
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<Advisor> {
  const { data, error } = await client
    .from('advisors')
    .update(patch)
    .eq('id', id)
    .select(COLUMNS)
    .single();

  if (error) throw error;
  return data as Advisor;
}

export async function deleteAdvisor(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.from('advisors').delete().eq('id', id);
  if (error) throw error;
}

// Promote a single advisor to default through the atomic `set_default_advisor`
// RPC (M13): demote + promote run in one PostgREST transaction, so the
// zero-default window of the previous two-request clear-then-set is gone.
// SECURITY INVOKER — RLS (advisors_admin_all) gates the writes; the RPC also
// requires the target to exist and be active.
export async function setDefaultAdvisor(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.rpc('set_default_advisor', { p_advisor_id: id });
  if (error) throw error;
}
